import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Footer im Format des neuen Designs (~/Projects/davidmedia-header-test, footer.fuss-bereich):
// links Marke + ein Satz, daneben Produkte · Hilfe · Rechtliches, darunter die Copyright-Zeile.
// Logo und Schriftzug wie oben in der Leiste (runde Plakette + DAVIDMEDIA in Syne) — die Demo
// hatte dort ein verzerrtes Bild. Läuft über die volle Breite mit denselben Rändern wie die Seiten.

const InstagramIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const SOZIAL = [
  { name: 'Instagram', icon: InstagramIcon, url: 'https://www.instagram.com/started.david' },
  { name: 'TikTok', icon: TikTokIcon, url: 'https://www.tiktok.com/@started.david' },
];

function Spalte({ titel, links }) {
  return (
    <div>
      <h4 className="font-manrope font-semibold text-[13px] text-[#F4F4F5] mt-1.5 mb-3.5">{titel}</h4>
      {links.map(([text, ziel, testid]) => (
        <Link key={ziel} to={ziel} data-testid={testid}
          className="block font-manrope text-sm text-[#8f8f99] hover:text-[#F4F4F5] transition-colors duration-200 mb-2.5">
          {text}
        </Link>
      ))}
    </div>
  );
}

const Footer = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const en = i18n.language === 'en' || location.pathname.startsWith('/en');
  const p = en ? '/en' : '';
  const T = (de, enText) => (en ? enText : de);

  return (
    <footer className="relative border-t border-white/10 bg-[#07070a] px-4 md:px-8 lg:px-12 xl:px-16 pt-14 pb-16">
      <div className="max-w-[2400px] mx-auto grid grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-8">
        <div className="col-span-2 lg:col-span-1">
          <Link to={en ? '/en' : '/'} className="inline-flex items-center gap-2.5 mb-4" aria-label="David Media">
            <span className="w-8 h-8 rounded-full overflow-hidden ring-2 ring-white/10 shrink-0">
              <img src="/logo-badge-192.webp" alt="" className="w-full h-full object-cover" />
            </span>
            <span className="font-syne font-bold text-base text-white">DAVIDMEDIA</span>
          </Link>
          <p className="font-manrope text-sm leading-relaxed text-[#8f8f99] max-w-[300px]">
            {T('Werkzeuge für After Effects. Gebaut im Schnitt, nicht im Büro.', 'Tools for After Effects. Built in the edit, not in an office.')}
          </p>
          <div className="flex gap-2 mt-5">
            {SOZIAL.map(({ name, icon: Icon, url }) => (
              <a key={name} href={url} target="_blank" rel="noopener noreferrer" aria-label={name} data-testid={`social-${name.toLowerCase()}`}
                className="w-9 h-9 rounded-full flex items-center justify-center border border-white/10 text-[#8f8f99] hover:text-[#F97316] hover:border-[#F97316]/40 transition-colors duration-200">
                <Icon />
              </a>
            ))}
          </div>
        </div>
        <Spalte titel={T('Produkte', 'Products')} links={[
          [T('Werkzeuge', 'Tools'), `${p}/shop`],
          ['All-in-One', `${p}/shop/all-in-one`],
          [T('Kostenlos', 'Free'), en ? '/en/free' : '/kostenlos'],
          ['Installer', `${p}/installer`],
        ]} />
        <Spalte titel={T('Hilfe', 'Help')} links={[
          [T('Support & FAQ', 'Support & FAQ'), `${p}/support`],
          [T('Mein Konto', 'My account'), '/aether', 'footer-login'],
        ]} />
        <Spalte titel={T('Rechtliches', 'Legal')} links={[
          [T('Impressum', 'Imprint'), en ? '/en/imprint' : '/impressum'],
          [T('Datenschutz', 'Privacy'), en ? '/en/privacy' : '/datenschutz'],
          [T('AGB', 'Terms'), en ? '/en/terms' : '/agb'],
          [T('Widerrufsbelehrung', 'Withdrawal policy'), en ? '/en/withdrawal' : '/widerruf'],
        ]} />
      </div>
      {/* Widerrufsfunktion nach § 356a BGB: muss auf jeder Seite ständig verfügbar und HERVORGEHOBEN sein —
          ein normaler Link in der Rechtliches-Liste reicht nicht. Deshalb eigene Zeile, orange, klar
          getrennt von AGB/Impressum. Pflicht-Beschriftung „Vertrag widerrufen“.
          Daneben, gleich hervorgehoben, der Kündigungsbutton nach § 312k BGB für das All-in-One-Abo:
          Pflicht-Beschriftung „Verträge hier kündigen“, führt ohne Login direkt zur Bestätigungsseite. */}
      <div className="max-w-[2400px] mx-auto mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row-reverse sm:items-center sm:justify-between gap-5">
        <div className="flex flex-wrap gap-3">
          <Link to={en ? '/en/withdraw' : '/vertrag-widerrufen'} data-testid="footer-vertrag-widerrufen"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-[#F97316]/80 bg-[#F97316]/[0.12] text-[#FB923C] font-manrope font-bold text-sm hover:bg-[#F97316]/20 hover:border-[#F97316] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F97316]/50">
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 14L4 9l5-5" /><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
            </svg>
            {T('Vertrag widerrufen', 'Withdraw from contract')}
          </Link>
          <Link to={en ? '/en/cancel-contract' : '/vertraege-kuendigen'} data-testid="footer-vertraege-kuendigen"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-[#F97316]/80 bg-[#F97316]/[0.12] text-[#FB923C] font-manrope font-bold text-sm hover:bg-[#F97316]/20 hover:border-[#F97316] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F97316]/50">
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9.5 12.5l5 5" /><path d="M14.5 12.5l-5 5" />
            </svg>
            {T('Verträge hier kündigen', 'Cancel contracts here')}
          </Link>
        </div>
        <div className="font-manrope text-xs text-[#5d5d66]">
          © {new Date().getFullYear()} David Ferfers · {T('Adobe und After Effects sind Marken von Adobe Inc.', 'Adobe and After Effects are trademarks of Adobe Inc.')}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
